import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useAnimatedPresence } from 'hooks/overlay/useAnimatedPresence';
import { useDrawerA11y } from 'hooks/overlay/useDrawerA11y';
import { cn } from 'utils/cn';

const PHASE_CLASS = {
  enter: 'ui-drawer--enter',
  idle: 'ui-drawer--idle',
  exit: 'ui-drawer--exit',
} as const;

export interface DrawerProps {
  open: boolean;
  onClose?: () => void;
  children: ReactNode;
  className?: string;
  backdropClassName?: string;
  panelClassName?: string;
  zIndexClass?: string;
  lockScroll?: boolean;
  usePortal?: boolean;
  closeOnBackdrop?: boolean;
  durationMs?: number;
  'aria-label'?: string;
  'aria-modal'?: boolean;
  role?: string;
  backdropAriaLabel?: string;
  blockEscape?: boolean;
  triggerRef?: RefObject<HTMLElement | null>;
  'data-leave-guard-skip'?: boolean;
}

/**
 * Right-side slide-in drawer with backdrop fade.
 */
export function Drawer({
  open,
  onClose,
  children,
  className,
  backdropClassName = 'bg-black/45 backdrop-blur-[1px]',
  panelClassName,
  zIndexClass = 'z-[10050]',
  lockScroll = true,
  usePortal = true,
  closeOnBackdrop = true,
  durationMs = 480,
  'aria-label': ariaLabel,
  'aria-modal': ariaModal = true,
  role = 'dialog',
  backdropAriaLabel = 'Close drawer',
  blockEscape = false,
  triggerRef,
  'data-leave-guard-skip': dataLeaveGuardSkip,
}: DrawerProps) {
  const { mounted, phase } = useAnimatedPresence(open, durationMs);
  const panelRef = useRef<HTMLElement>(null);

  useDrawerA11y(mounted && phase === 'idle', panelRef, { onClose, triggerRef, blockEscape });

  useEffect(() => {
    if (!mounted || !lockScroll) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mounted, lockScroll]);

  if (!mounted) return null;

  const content = (
    <div
      className={cn('ui-drawer fixed inset-0', zIndexClass, PHASE_CLASS[phase], className)}
      data-leave-guard-skip={dataLeaveGuardSkip || undefined}
    >
      <button
        type="button"
        className={cn('ui-drawer__backdrop fixed inset-0', backdropClassName)}
        aria-label={backdropAriaLabel}
        onClick={closeOnBackdrop ? onClose : undefined}
        tabIndex={closeOnBackdrop ? 0 : -1}
      />
      <aside
        ref={panelRef}
        role={role}
        aria-modal={ariaModal}
        aria-label={ariaLabel}
        className={cn(
          'ui-drawer__panel fixed inset-y-0 right-0 z-[1] flex min-w-0 flex-col shadow-modal outline-none',
          panelClassName
        )}
        tabIndex={-1}
      >
        {children}
      </aside>
    </div>
  );

  if (usePortal && typeof document !== 'undefined') {
    return createPortal(content, document.body);
  }

  return content;
}
